import { getApiConfig } from "@/lib/api/config";

const SAML_CALLBACK_PATH = "/auth/saml/callback";

/** SPA route that receives `?accessToken=&refreshToken=` after backend SAML ACS. */
export function getSamlCallbackPath(): string {
  return SAML_CALLBACK_PATH;
}

export function getSamlCallbackUrl(): string {
  if (typeof window !== "undefined") {
    return `${window.location.origin}${SAML_CALLBACK_PATH}`;
  }
  return SAML_CALLBACK_PATH;
}

/** Backend SP-initiated login URL (Azure redirect starts here). */
export function getSamlLoginUrl(): string {
  const { authLoginUrl, baseUrl } = getApiConfig();
  if (authLoginUrl.trim()) return authLoginUrl.trim();
  if (baseUrl.trim()) return `${baseUrl.replace(/\/$/, "")}/api/auth/saml/login`;
  return "/api/auth/saml/login";
}

/** Backend SP metadata XML (for Azure Enterprise App setup — not a SPA route). */
export function getSamlMetadataUrl(): string {
  const { baseUrl } = getApiConfig();
  const root = baseUrl.replace(/\/$/, "");
  return root
    ? `${root}/api/auth/saml/metadata`
    : "/api/auth/saml/metadata";
}

function normalizePath(pathname: string): string {
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

export function isSamlCallbackPath(pathname: string): boolean {
  const normalized = normalizePath(pathname);
  return (
    normalized.startsWith("/auth/saml/callback/") ||
    normalized.startsWith("/auth/callback/")
  );
}

export function hasSamlHandoffParams(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return Boolean(
    params.get("accessToken")?.trim() ||
      params.get("refreshToken")?.trim() ||
      params.get("error")?.trim(),
  );
}

export function clearSamlHandoffParams(): void {
  if (typeof window === "undefined") return;
  window.history.replaceState({}, document.title, window.location.pathname);
}

/**
 * If the API redirects to the legacy /auth/callback path, forward to /auth/saml/callback.
 */
export function redirectLegacyCallbackIfNeeded(): boolean {
  if (typeof window === "undefined") return false;

  const path = normalizePath(window.location.pathname);
  if (!path.startsWith("/auth/callback/")) return false;
  if (path.startsWith("/auth/saml/callback/")) return false;
  if (!hasSamlHandoffParams()) return false;

  const target = new URL(getSamlCallbackUrl());
  target.search = window.location.search;
  window.location.replace(target.href);
  return true;
}
